import React from 'react';
import { graphql } from 'react-relay';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import Tag from '@atlaskit/tag/removable-tag';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Inline from '@atlaskit/primitives/inline';
import { xcss } from '@atlaskit/primitives/xcss';
import Pressable from '@atlaskit/primitives/pressable';
import TagIcon from '@atlaskit/icon/core/tag';
import GlobeIcon from '@atlaskit/icon/core/globe';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { useShowDetailsPanel } from '@confluence/details-panel/entry-points/useShowDetailsPanel.ts';
import { EditLabelDialogLoader } from '@confluence/labels/entry-points/EditLabelDialogLoader.ts';
import {
	useExternalLabelsFragment,
	useExternalLabelsFragmentVariables,
} from '@confluence/labels/entry-points/useExternalLabels.ts';
import { VIEW_PAGE_LABELS_IN_FOOTER_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceFailure,
	ExperienceSuccess,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useShortcutStrings } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import { UNIVERSAL_LABELS_SHORTCUT } from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	FooterLabelsQuery,
	FooterLabelsQueryVariables,
} from './__types__/FooterLabelsQuery.ts';
export const FooterLabels = (): React.JSX.Element | null => {
	const [contentId = ''] = usePageContentId();
	const [spaceKey = ''] = usePageSpaceKey();
	const { showModal } = useDialogs();
	const { showDetailsPanel } = useShowDetailsPanel();

	const labelsShortcutStrings = useShortcutStrings({ shortcut: UNIVERSAL_LABELS_SHORTCUT });
	// Managed (external) labels ride on this query via the `useExternalLabels_query`
	// fragment; Relay dedups against the warmed store, so no extra request.
	const externalLabelsVariables = useExternalLabelsFragmentVariables(contentId);
	const { loading, data, error } = useQueryInterop<FooterLabelsQuery, FooterLabelsQueryVariables>(
		graphql`
			query FooterLabelsQuery(
				$contentId: ID!
				$includeExternalLabels: Boolean = false
				$externalLabelsCloudId: ID!
				$externalLabelsAri: ID!
			) {
				singleContent(id: $contentId) {
					id
					type
					operations {
						operation
						targetType
					}
					labels(orderBy: { sortField: LABELLING_CREATIONDATE, direction: ASCENDING }) {
						nodes {
							id
							label
							name
							prefix
						}
					}
				}
				...useExternalLabels_query
					@arguments(cloudId: $externalLabelsCloudId, id: $externalLabelsAri)
					@alias(as: "externalLabelsFragment")
					@include(if: $includeExternalLabels)
			}
		`,
		{
			variables: {
				contentId,
				includeExternalLabels: externalLabelsVariables.includeExternalLabels,
				externalLabelsCloudId: externalLabelsVariables.cloudId,
				externalLabelsAri: externalLabelsVariables.id,
			},
		},
	);

	const intl = useIntl();
	// Read from the aliased fragment; empty list when the gate is off (re-checked
	// inline at the render sites). The fragment read is store-subscribed, so an
	// apply/remove from any surface (which writes the normalized
	// `confluence_externalLabels` record) reflects here immediately — no overlay needed.
	const externalLabels = useExternalLabelsFragment(data?.externalLabelsFragment);

	if (loading) {
		return null;
	}

	if (error) {
		return (
			<>
				<ExperienceFailure name={VIEW_PAGE_LABELS_IN_FOOTER_EXPERIENCE} error={error} />
				<ErrorDisplay error={error} />
			</>
		);
	}

	const content = data?.singleContent;

	const hasEditPermissions = Boolean(
		content?.operations?.find(
			(item) => item?.operation === 'update' && item?.targetType === content?.type,
		),
	);

	const labels =
		content?.labels?.nodes?.filter((node) => node?.name !== 'favourite' || node?.prefix !== 'my') ||
		[];

	const maxVisibleLabels = expValEquals('confluence_footer_reactions_redesign', 'isEnabled', true)
		? 5
		: 2;

	// Managed labels render first (matching the dialog / details panel order).
	// Gated inline so nothing leaks when off.
	const managedLabels = fg('confluence_managed_external_labels_endpoint') ? externalLabels : [];

	// Unified list (managed first) so the "max N + overflow" rule spans both kinds.
	const footerTags: Array<{ key: string; name: string; isManaged: boolean }> = [
		...managedLabels.map((label) => ({
			key: `managed-${label.id}`,
			name: label.name as string,
			isManaged: true,
		})),
		...labels.map((label) => ({
			key: String(label?.id),
			name: label?.name as string,
			isManaged: false,
		})),
	];

	if (footerTags.length === 0) {
		if (!hasEditPermissions) {
			// don't show anything if user doesn't have edit permissions
			return (
				<ExperienceSuccess
					name={VIEW_PAGE_LABELS_IN_FOOTER_EXPERIENCE}
					attributes={{ hasEditPermissions, labelsCount: labels.length }}
				/>
			);
		}

		return (
			<Tooltip
				content={
					expValEquals('confluence_comments_cohiti_footer_tooltips', 'isEnabled', true) ? (
						<FormattedMessage {...i18n.addLabelTooltip} />
					) : undefined
				}
				shortcut={labelsShortcutStrings}
				position="top"
			>
				<Pressable
					xcss={addLabelsButton}
					onClick={() =>
						showModal(EditLabelDialogLoader, {
							contentId,
							spaceKey,
							source: 'labelsFooter',
						})
					}
				>
					<TagIcon label="" />
					<FormattedMessage {...i18n.addLabels} />
				</Pressable>
				<ExperienceSuccess
					name={VIEW_PAGE_LABELS_IN_FOOTER_EXPERIENCE}
					attributes={{ labelsCount: labels.length, hasEditPermissions }}
				/>
			</Tooltip>
		);
	}

	return (
		<Tooltip
			content={
				expValEquals('confluence_comments_cohiti_footer_tooltips', 'isEnabled', true) ? (
					<FormattedMessage {...i18n.manageLabelsTooltip} />
				) : undefined
			}
			position="top"
		>
			<Pressable
				xcss={labelsContainer}
				onClick={() => {
					showDetailsPanel({ highlightLabels: true });
				}}
			>
				{footerTags.slice(0, maxVisibleLabels).map((tag) =>
					tag.name ? (
						tag.isManaged ? (
							// Blue tag + globe icon. No `migration_fallback`: the Lozenge
							// fallback drops `elemBefore` (the icon).
							<Tag
								key={tag.key}
								text={tag.name}
								color="blue"
								elemBefore={
									<GlobeIcon
										label={intl.formatMessage(i18n.managedLabelIconLabel)}
										size="small"
										color={token('color.icon.subtle')}
									/>
								}
								hasMargin={false}
								isRemovable={false}
							/>
						) : expValEquals('confluence_footer_reactions_redesign', 'isEnabled', true) ? (
							// The new chip layout: `Tag`'s lozenge fallback renders a bordered,
							// unfilled chip; render a plain filled chip instead to match the
							// solid-fill design.
							<Inline key={tag.key} xcss={newFooterLabelChip}>
								{tag.name}
							</Inline>
						) : (
							<Tag
								key={tag.key}
								text={tag.name}
								migration_fallback="lozenge"
								hasMargin={false}
								isRemovable={false}
							/>
						)
					) : null,
				)}
				{footerTags.length > maxVisibleLabels && (
					<Inline
						xcss={
							expValEquals('confluence_footer_reactions_redesign', 'isEnabled', true)
								? additionalLabelsNew
								: additionalLabels
						}
					>
						+{footerTags.length - maxVisibleLabels}
					</Inline>
				)}
			</Pressable>
			<ExperienceSuccess
				name={VIEW_PAGE_LABELS_IN_FOOTER_EXPERIENCE}
				attributes={{ labelsCount: footerTags.length }}
			/>
		</Tooltip>
	);
};

const labelsContainer = xcss({
	display: 'flex',
	alignItems: 'center',
	gap: 'space.100',
	paddingInline: 'space.100',
	paddingBlock: 'space.050',
	height: '32px',
	backgroundColor: 'color.background.neutral.subtle',
	transition: token('motion.button.hovered'),
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
		transition: token('motion.button.pressed'),
	},
});

const additionalLabels = xcss({
	font: 'font.body.small',
	fontWeight: 'font.weight.bold',
	paddingInline: 'space.050',
});

// New layout only: matches the solid-fill chips' regular weight, rather than the previous tags'
// bold "+N" — kept separate from `additionalLabels` above so that weight is unaffected.
const additionalLabelsNew = xcss({
	font: 'font.body.small',
	paddingInline: 'space.050',
});

// Solid grey fill, no border — see the comment where this is used, above, for why it bypasses `Tag`.
const newFooterLabelChip = xcss({
	display: 'inline-flex',
	alignItems: 'center',
	paddingInline: 'space.100',
	paddingBlock: 'space.025',
	borderRadius: 'radius.small',
	backgroundColor: 'color.background.neutral',
	color: 'color.text',
	font: 'font.body.small',
});

const addLabelsButton = xcss({
	display: 'flex',
	gap: 'space.100',
	alignItems: 'center',
	paddingInline: 'space.150',
	borderRadius: 'radius.small',
	color: 'color.text.subtle',
	font: 'font.body',
	height: '32px',
	backgroundColor: 'color.background.neutral.subtle',
	transition: token('motion.button.hovered'),
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
		transition: token('motion.button.pressed'),
	},
});

const i18n = defineMessages({
	addLabels: {
		id: 'labels-footer.addLabels',
		defaultMessage: 'Add labels',
		description: 'Button text to add labels to a page. On click it opens the add labels dialog.',
	},
	addLabelTooltip: {
		id: 'labels-footer.addLabel-tooltip',
		defaultMessage: 'Add label',
		description: 'Tooltip for shorcut visualizer when button opens the add labels dialog',
	},
	manageLabelsTooltip: {
		id: 'labels-footer.manageLabels-tooltip',
		defaultMessage: 'Manage labels',
		description: 'Tooltip used when button opens the sidebar for managing current labels',
	},
	managedLabelIconLabel: {
		id: 'labels-footer.managed-label-icon-label',
		defaultMessage: 'Managed label',
		description:
			'Accessible label for the globe icon shown on admin-managed (external) labels to distinguish them from regular labels',
	},
});
